import { useLingui } from '@lingui/react/macro'
import { useProvinces } from '@/hooks/use-address-lists'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { hasFieldError, useFieldContext } from './shared'
import { NativeSelectFieldShell } from './native-select-shell'

/**
 * `ProvinceField` as a native `<select>`, so autofill fills it from `address-level1` (the Spanish provincia).
 * Option values are NAMES, which is what autofill matches; the field still stores the ISO 3166-2 code.
 */
export default function NativeProvinceField(props: {
  label: string
  className?: string
  placeholder?: string
  optional?: boolean
  orientation?: 'vertical' | 'horizontal'
  autoComplete?: string
  country?: string | null
}) {
  const { t } = useLingui()
  const field = useFieldContext<string>()
  const options = useProvinces(props.country)
  const selected = options.find((o) => o.value === field.state.value)

  return (
    <NativeSelectFieldShell
      label={props.label}
      className={props.className}
      optional={props.optional}
      orientation={props.orientation}
      readOnlyLabel={selected?.label}
    >
      <NativeSelect
        className="w-full"
        id={field.name}
        name={field.name}
        autoComplete={props.autoComplete ?? 'address-level1'}
        aria-invalid={hasFieldError(field.state.meta) || undefined}
        value={selected?.label ?? ''}
        onBlur={field.handleBlur}
        // By index, not by name: one country can repeat a name ("Merkez" names 38 Turkish provinces).
        onChange={(event) => field.handleChange(options[event.target.selectedIndex - 1]?.value ?? '')}
      >
        <NativeSelectOption value="">{props.placeholder ?? t`Select a province`}</NativeSelectOption>
        {options.map((item) => (
          <NativeSelectOption key={item.value} value={item.label}>
            {item.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </NativeSelectFieldShell>
  )
}
